import type { AuthStatus } from "./context";

/**
 * Where the product's two "go and use it" controls point.
 *
 * The site was built for one reader — someone who has not signed up — and every
 * route into the product went through the sales funnel. Once you HAD subscribed
 * there was no link into /app anywhere on the marketing side: the hero's primary
 * CTA went to /pricing, the nav's Models link went to the marketing anchor
 * `/#the-models`, and the one auth-aware control went to /app/account, which
 * itself offered only Sign out and, again, Pricing.
 *
 * A paying customer could reach the thing they were paying for by typing the URL
 * or by the post-sign-in redirect, and no other way. Worse, the hero sold to
 * them: clicking "See the models" as a subscriber landed on a page quoting them
 * a price for what they already own.
 *
 * One rule, in one place, so the three call sites cannot drift: signed in means
 * the product, signed out means the funnel.
 *
 * `loading` deliberately resolves to the signed-OUT destination. It is only the
 * boot window, and a link is not a redirect — the guards re-decide on arrival,
 * so the cost of being wrong for a moment is a hop through /pricing rather than
 * a paying user bounced to /login.
 *
 * ⚠️ `modelsHref`'s AUTHENTICATED TARGET MOVED TO `/app/models` (Ned,
 * 2026-09-07). `/app` is the free fixtures landing now (`routes/Home.tsx`),
 * not the model grid — a nav item labelled "Models" that opened a fixtures
 * board would be the same mismatch the marketing anchor already avoids by
 * naming itself precisely. `primaryCtaHref` stays on `/app`: it is the
 * generic "go use the product" CTA, and the product's own landing IS where
 * it should go.
 *
 * ⚠️ THE SIGNED-OUT ANCHOR MOVED TO `/why-kev#the-models` the same day the
 * fixture list took `/` from the marketing page (Landing.tsx). The anchor
 * still names a section of Landing — it just has to name the page Landing
 * lives on now, or it resolves against the fixture list and scrolls nowhere.
 */
export function modelsHref(status: AuthStatus): string {
  return status === "authenticated" ? "/app/models" : "/why-kev#the-models";
}

export function primaryCtaHref(status: AuthStatus): string {
  return status === "authenticated" ? "/app" : "/pricing";
}
